/**
 * Admin Dashboard Page
 *
 * Main dashboard for the director (Elienai) to manage the school.
 * Provides overview and quick access to teachers, students, and classes management.
 */

import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useAdminClassGroups,
  useAdminStudents,
  useAdminTeachers,
} from "../../hooks/useAdmin";
import { useAuthStore } from "../../store";

export function AdminDashboardPage() {
  const { user } = useAuthStore();

  const { data: teachers, isLoading: loadingTeachers } = useAdminTeachers();
  const { data: students, isLoading: loadingStudents } = useAdminStudents();
  const { data: classes, isLoading: loadingClasses } = useAdminClassGroups();

  const isLoading = loadingTeachers || loadingStudents || loadingClasses;

  // Calculate stats
  const activeStudents = students?.filter((s) => s.isActive).length || 0;
  const inactiveStudents = students?.filter((s) => !s.isActive).length || 0;
  const activeTeachers = teachers?.filter((t) => t.isActive).length || 0;
  const totalClasses = classes?.length || 0;

  // Get students without class
  const studentsWithoutClass =
    students?.filter((s) => s.isActive && !s.classGroup).length || 0;

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 text-earth-700 text-sm mb-2">
            <i className="ri-admin-line"></i>
            <span>Admin Dashboard</span>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
              Director
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            Hello, {user?.firstName || "Director"}
          </h1>
          <p className="text-earth-700 text-sm mt-1">
            Manage Beyond Class teachers, students and classes
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        )}

        {!isLoading && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-star-line text-amber-600 text-lg"></i>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-earth-800">
                  {activeTeachers}
                </p>
                <p className="text-earth-700 text-sm">Teachers</p>
              </div>

              <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-3-line text-emerald-600 text-lg"></i>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-earth-800">
                  {activeStudents}
                </p>
                <p className="text-earth-700 text-sm">Active students</p>
              </div>

              <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-olive-100 rounded-lg flex items-center justify-center">
                    <i className="ri-group-line text-olive-600 text-lg"></i>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-earth-800">
                  {totalClasses}
                </p>
                <p className="text-earth-700 text-sm">Classes</p>
              </div>

              <div className="bg-white rounded-xl border border-earth-200 p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-unfollow-line text-red-600 text-lg"></i>
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-earth-800">
                  {studentsWithoutClass}
                </p>
                <p className="text-earth-700 text-sm">No class</p>
              </div>
            </div>

            {/* Management Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 sm:mb-8">
              {/* Teachers Card */}
              <Link
                to="/admin/teachers"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-amber-300 hover:bg-amber-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                    <i className="ri-user-star-line text-amber-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-amber-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-amber-800 mb-1">
                  Teachers
                </h3>
                <p className="text-sm text-earth-700 mb-3">
                  Create, edit and manage teachers
                </p>
                <div className="mt-auto pt-3 border-t border-earth-100">
                  <span className="text-sm text-earth-600">
                    {activeTeachers} active
                  </span>
                </div>
              </Link>

              {/* Students Card */}
              <Link
                to="/admin/students"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <i className="ri-user-3-line text-emerald-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-emerald-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-emerald-800 mb-1">
                  Students
                </h3>
                <p className="text-sm text-earth-700 mb-3">
                  Create, edit and move students between classes
                </p>
                <div className="mt-auto pt-3 border-t border-earth-100 flex items-center gap-3">
                  <span className="text-sm text-earth-600">
                    {activeStudents} active
                  </span>
                  {inactiveStudents > 0 && (
                    <>
                      <span className="text-earth-300">•</span>
                      <span className="text-sm text-earth-400">
                        {inactiveStudents} inactive
                      </span>
                    </>
                  )}
                </div>
              </Link>

              {/* Classes Card */}
              <Link
                to="/admin/classes"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-olive-300 hover:bg-olive-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-olive-100 rounded-xl flex items-center justify-center group-hover:bg-olive-200 transition-colors">
                    <i className="ri-group-line text-olive-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-olive-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-olive-800 mb-1">
                  Classes
                </h3>
                <p className="text-sm text-earth-700 mb-3">
                  Create classes, set schedules and links
                </p>
                <div className="mt-auto pt-3 border-t border-earth-100">
                  <span className="text-sm text-earth-600">
                    {totalClasses} class{totalClasses !== 1 ? "es" : ""}
                  </span>
                </div>
              </Link>
            </div>

            {/* Content Management */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 sm:mb-8">
              {/* Levels Card */}
              <Link
                to="/admin/levels"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-olive-300 hover:bg-olive-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-olive-100 rounded-xl flex items-center justify-center group-hover:bg-olive-200 transition-colors">
                    <i className="ri-bar-chart-grouped-line text-olive-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-olive-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-olive-800 mb-1">
                  Levels
                </h3>
                <p className="text-sm text-earth-700">
                  Add levels and edit their shared resources
                </p>
              </Link>

              {/* Lessons Card */}
              <Link
                to="/admin/lessons"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-olive-300 hover:bg-olive-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-olive-100 rounded-xl flex items-center justify-center group-hover:bg-olive-200 transition-colors">
                    <i className="ri-book-open-line text-olive-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-olive-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-olive-800 mb-1">
                  Lessons
                </h3>
                <p className="text-sm text-earth-700">
                  Create and edit lessons per level (attach Drive links)
                </p>
              </Link>

              {/* Homeworks Card */}
              <Link
                to="/admin/homeworks"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-orange-300 hover:bg-orange-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                    <i className="ri-todo-line text-orange-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-orange-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-orange-800 mb-1">
                  Homeworks
                </h3>
                <p className="text-sm text-earth-700">
                  Set the Study Planner link of each level
                </p>
              </Link>

              {/* Extra Materials Card */}
              <Link
                to="/admin/extra-materials"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-olive-300 hover:bg-olive-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-olive-100 rounded-xl flex items-center justify-center group-hover:bg-olive-200 transition-colors">
                    <i className="ri-attachment-2 text-olive-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-olive-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-olive-800 mb-1">
                  Extra Materials
                </h3>
                <p className="text-sm text-earth-700">
                  Publish homework / links to all, a level, or a class
                </p>
              </Link>

              {/* Beyond Readers Card */}
              <Link
                to="/admin/readers"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-rose-300 hover:bg-rose-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center group-hover:bg-rose-200 transition-colors">
                    <i className="ri-book-read-line text-rose-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-rose-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-rose-800 mb-1">
                  Beyond Readers
                </h3>
                <p className="text-sm text-earth-700">
                  Edit the monthly book club books (cover, description, link)
                </p>
              </Link>

              {/* Contracts Card */}
              <Link
                to="/admin/contracts"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-amber-300 hover:bg-amber-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                    <i className="ri-file-text-line text-amber-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-amber-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-amber-800 mb-1">
                  Contracts
                </h3>
                <p className="text-sm text-earth-700">
                  Update the contract link; students re-sign the new version
                </p>
              </Link>

              {/* Pricing Card */}
              <Link
                to="/admin/pricing"
                className="flex flex-col p-5 bg-white rounded-xl border border-earth-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <i className="ri-money-dollar-circle-line text-emerald-600 text-xl"></i>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400 text-xl group-hover:text-emerald-600 transition-colors"></i>
                </div>
                <h3 className="font-semibold text-earth-800 group-hover:text-emerald-800 mb-1">
                  Class Pricing
                </h3>
                <p className="text-sm text-earth-700">
                  Set class prices by level, type or specific classes
                </p>
              </Link>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-earth-200 p-5">
              <h2 className="text-lg font-semibold text-earth-800 mb-4">
                Quick Actions
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Link
                  to="/admin/classes?action=create"
                  className="flex items-center gap-3 p-3 bg-olive-50 hover:bg-olive-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-olive-200 rounded-lg flex items-center justify-center">
                    <i className="ri-add-line text-olive-700"></i>
                  </div>
                  <span className="text-sm font-medium text-olive-800">
                    New Class
                  </span>
                </Link>

                <Link
                  to="/admin/students?action=create"
                  className="flex items-center gap-3 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-emerald-200 rounded-lg flex items-center justify-center">
                    <i className="ri-user-add-line text-emerald-700"></i>
                  </div>
                  <span className="text-sm font-medium text-emerald-800">
                    New Student
                  </span>
                </Link>

                <Link
                  to="/admin/teachers?action=create"
                  className="flex items-center gap-3 p-3 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-amber-200 rounded-lg flex items-center justify-center">
                    <i className="ri-user-star-line text-amber-700"></i>
                  </div>
                  <span className="text-sm font-medium text-amber-800">
                    New Teacher
                  </span>
                </Link>

                <Link
                  to="/teacher"
                  className="flex items-center gap-3 p-3 bg-earth-50 hover:bg-earth-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-earth-200 rounded-lg flex items-center justify-center">
                    <i className="ri-eye-line text-earth-700"></i>
                  </div>
                  <span className="text-sm font-medium text-earth-700">
                    View as Teacher
                  </span>
                </Link>
              </div>
            </div>

            {/* Recent Activity / Alerts */}
            {studentsWithoutClass > 0 && (
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-200 rounded-lg flex items-center justify-center shrink-0">
                    <i className="ri-alert-line text-amber-700"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-800">
                      Students without class
                    </h3>
                    <p className="text-sm text-amber-700 mt-0.5">
                      There {studentsWithoutClass === 1 ? "is" : "are"}{" "}
                      {studentsWithoutClass} active student
                      {studentsWithoutClass !== 1 ? "s" : ""} without an
                      assigned class.
                    </p>
                    <Link
                      to="/admin/students?filter=no-class"
                      className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-amber-800 hover:text-amber-900"
                    >
                      View students
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboardPage;
